/* ============================================================
   Chpters Quiz — chpters-quiz.js
   Upload to: Shopify Admin → Themes → Edit code → Assets
   ============================================================
   Supports:
   - Personalised name on quiz + result card
   - 5-question personality quiz
   - Canvas result card (downloadable PNG)
   - Email capture with validation
   - Klaviyo list subscribe (set List ID in theme customizer)
   - Chpter-specific discount code reveal
   - Copy tag / save image sharing
   ============================================================ */

(function () {
  'use strict';

  /* ── Quiz questions ── */
  var QUESTIONS = [
    {
      q: "How would you describe your mornings lately?",
      opts: [
        { t: "Solo coffee, peaceful — it's all mine", c: "single" },
        { t: "Waking up next to someone who makes me smile", c: "love" },
        { t: "Chaotic but together — somebody always needs something", c: "parent" },
        { t: "Alarm before sunrise. The grind doesn't wait", c: "hustle" },
        { t: "Lazy mornings with my partner, no rush", c: "married" }
      ]
    },
    {
      q: "What's taking up most of your mental space right now?",
      opts: [
        { t: "Figuring out who I am and what I actually want", c: "single" },
        { t: "This person I can't stop thinking about", c: "love" },
        { t: "Our home, our future, us", c: "married" },
        { t: "Nap schedules, milestones, and unconditional love", c: "parent" },
        { t: "Building something — money, legacy, freedom", c: "hustle" }
      ]
    },
    {
      q: "Pick the vibe that hits closest to home:",
      opts: [
        { t: "Main character energy — solo era, no apologies", c: "single" },
        { t: "Butterflies and late night texts", c: "love" },
        { t: "Vows, forever, two becoming one", c: "married" },
        { t: "Tiny hands, big heart, zero sleep", c: "parent" },
        { t: "Rise and grind — building something real", c: "hustle" }
      ]
    },
    {
      q: "Your friends would describe you as:",
      opts: [
        { t: "The free spirit — always down for a spontaneous trip", c: "single" },
        { t: "The romantic — all heart, always in love", c: "love" },
        { t: "The family-first one — home is everything", c: "married" },
        { t: "The devoted parent — kids come first always", c: "parent" },
        { t: "The ambitious one — always working on a new idea", c: "hustle" }
      ]
    },
    {
      q: "What would your perfect weekend look like?",
      opts: [
        { t: "Solo adventure or exploring with new people", c: "single" },
        { t: "Date night, long walks, just the two of us", c: "love" },
        { t: "Staying in, cooking together, building our world", c: "married" },
        { t: "Family day out — watching them experience everything", c: "parent" },
        { t: "Working on my project, podcasts, leveling up", c: "hustle" }
      ]
    }
  ];

  /* ── Chpter data ── */
  var CHAPTERS = {
    single: {
      name: "The Single Chpter",
      tag: "#TheSingleChpter",
      icon: "🦋",
      accent: "#7F77DD",
      light: "#EEEDFE",
      dark: "#3C3489",
      mid: "#534AB7",
      captureBg: "#EEEDFE",
      captureBorder: "#AFA9EC",
      btnBg: "#7F77DD",
      codeBg: "#CECBF6",
      codeBorder: "#7F77DD",
      codeText: "#26215C",
      codeBtnColor: "#3C3489",
      sub: "You're in your independence era — self-discovery, freedom, and writing your own story on your own terms.",
      traits: [
        "Solo adventures & main character energy",
        "Dating life and connection on your own terms",
        "Finding yourself before finding your person",
        "Freedom that most people forget to appreciate"
      ],
      collectionKey: "url_single"
    },
    love: {
      name: "The Love Chpter",
      tag: "#TheLoveChpter",
      icon: "💕",
      accent: "#D4537E",
      light: "#FBEAF0",
      dark: "#72243E",
      mid: "#993556",
      captureBg: "#FBEAF0",
      captureBorder: "#ED93B1",
      btnBg: "#D4537E",
      codeBg: "#F4C0D1",
      codeBorder: "#D4537E",
      codeText: "#4B1528",
      codeBtnColor: "#72243E",
      sub: "Love is in full bloom — new romance, butterflies, and someone who makes everything feel better.",
      traits: [
        "New love energy and late night conversations",
        "Couples milestones worth celebrating",
        "Anniversary moments worth remembering",
        "Wearing your heart on your sleeve — literally"
      ],
      collectionKey: "url_love"
    },
    married: {
      name: "The Married Chpter",
      tag: "#TheMarriedChpter",
      icon: "💍",
      accent: "#1D9E75",
      light: "#E1F5EE",
      dark: "#085041",
      mid: "#0F6E56",
      captureBg: "#E1F5EE",
      captureBorder: "#5DCAA5",
      btnBg: "#1D9E75",
      codeBg: "#9FE1CB",
      codeBorder: "#1D9E75",
      codeText: "#04342C",
      codeBtnColor: "#085041",
      sub: "You found your person and built a life together. This chpter is about partnership, home, and forever.",
      traits: [
        "Partnership, patience, and deep love",
        "Building a home and shared dreams",
        "Wedding celebrations & anniversary milestones",
        "Two people, one beautiful story"
      ],
      collectionKey: "url_married"
    },
    parent: {
      name: "The Parent Chpter",
      tag: "#TheParentChpter",
      icon: "👶",
      accent: "#EF9F27",
      light: "#FAEEDA",
      dark: "#633806",
      mid: "#854F0B",
      captureBg: "#FAEEDA",
      captureBorder: "#EF9F27",
      btnBg: "#BA7517",
      codeBg: "#FAC775",
      codeBorder: "#BA7517",
      codeText: "#412402",
      codeBtnColor: "#633806",
      sub: "You're someone's whole world. This chpter is messy, exhausting, and the most meaningful thing you'll ever do.",
      traits: [
        "New parent survival mode — and loving it",
        "Watching tiny humans become real people",
        "Unconditional love that rewires your whole brain",
        "Parenting wins, fails, and everything between"
      ],
      collectionKey: "url_parent"
    },
    hustle: {
      name: "The Hustle Chpter",
      tag: "#TheHustleChpter",
      icon: "🔥",
      accent: "#D85A30",
      light: "#FAECE7",
      dark: "#712B13",
      mid: "#993C1D",
      captureBg: "#FAECE7",
      captureBorder: "#F0997B",
      btnBg: "#D85A30",
      codeBg: "#F5C4B3",
      codeBorder: "#D85A30",
      codeText: "#4A1B0C",
      codeBtnColor: "#712B13",
      sub: "You're building something. Career, business, legacy — the grind is real and so is the vision.",
      traits: [
        "Entrepreneur and side hustle energy",
        "Ambition that doesn't clock out",
        "Career moves and money goals",
        "Building the life most people only dream about"
      ],
      collectionKey: "url_hustle"
    }
  };

  /* ── Canvas helpers ── */
  function wrapText(ctx, text, x, y, maxW, lineH) {
    var words = text.split(' ');
    var line = '';
    for (var i = 0; i < words.length; i++) {
      var test = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(test).width > maxW && line) {
        ctx.fillText(line, x, y);
        line = words[i];
        y += lineH;
      } else {
        line = test;
      }
    }
    if (line) ctx.fillText(line, x, y);
    return y;
  }

  function drawCard(canvas, ch, userName) {
    var ctx = canvas.getContext('2d');
    var W = 750, H = 750;
    canvas.width = W;
    canvas.height = H;

    ctx.fillStyle = ch.light;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = ch.accent + '22';
    ctx.beginPath(); ctx.arc(W, 0, 320, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(0, H, 280, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = ch.accent + '18';
    ctx.beginPath(); ctx.arc(W / 2, H / 2, 200, 0, Math.PI * 2); ctx.fill();

    ctx.font = 'bold 15px Helvetica Neue, sans-serif';
    ctx.fillStyle = ch.mid;
    ctx.textAlign = 'center';
    ctx.fillText('CHPTERS.COM', W / 2, 58);

    ctx.font = '100px Helvetica Neue, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(ch.icon, W / 2, 215);

    ctx.fillStyle = ch.dark;
    ctx.font = 'bold 52px Helvetica Neue, sans-serif';
    ctx.fillText(ch.name, W / 2, 295);

    ctx.fillStyle = ch.mid;
    ctx.font = 'bold 28px Helvetica Neue, sans-serif';
    ctx.fillText(ch.tag, W / 2, 345);

    ctx.fillStyle = ch.dark + 'cc';
    ctx.font = '500 22px Helvetica Neue, sans-serif';
    ctx.fillText((userName || 'My') + "'s chpter", W / 2, 400);

    ctx.strokeStyle = ch.accent + '55';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(80, 430); ctx.lineTo(670, 430); ctx.stroke();

    ctx.fillStyle = ch.dark + 'bb';
    ctx.font = '19px Helvetica Neue, sans-serif';
    ctx.textAlign = 'center';
    wrapText(ctx, ch.sub, W / 2, 468, 560, 30);

    ctx.fillStyle = ch.dark + '44';
    ctx.font = '500 17px Helvetica Neue, sans-serif';
    ctx.fillText('chpters.com — gear for every chpter of life', W / 2, H - 36);
  }

  /* ── Klaviyo subscribe ── */
  function klaviyoSubscribe(email, name, chpterName, listId, publicKey, onDone) {
    if (!listId || !publicKey) { onDone(null); return; }
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Chpters Quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: name,
                properties: {
                  chpter: chpterName
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId
            }
          }
        }
      }
    };
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(publicKey), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'revision': '2026-04-15'
      },
      body: JSON.stringify(payload)
    })
      .then(function(r) {
        if (r.status === 202) {
          console.log('Klaviyo: subscribed successfully');
        } else {
          r.text().then(function(t) { console.warn('Klaviyo non-202:', r.status, t); });
        }
        onDone(null);
      })
      .catch(function(e) {
        console.warn('Klaviyo subscribe failed:', e);
        onDone(e);
      });
  }

  /* ── Main init ── */
  function init(sectionId) {
    var root = document.getElementById('chpters-quiz-' + sectionId);
    if (!root) return;

    /* Read settings from data attributes */
    var settings = {
      klaviyoListId: root.dataset.klaviyoList || '',
      klaviyoPublicKey: root.dataset.klaviyoPublicKey || '',
      showDiscount: root.dataset.showDiscount !== 'false',
      codes: {
        single: root.dataset.discountSingle || 'SINGLE15',
        love: root.dataset.discountLove || 'LOVE15',
        married: root.dataset.discountMarried || 'MARRIED15',
        parent: root.dataset.discountParent || 'PARENT15',
        hustle: root.dataset.discountHustle || 'HUSTLE15'
      }
    };

    /* Element helpers */
    function el(id) { return document.getElementById(id + '-' + sectionId); }
    function showScreen(name) {
      root.querySelectorAll('.cq-screen').forEach(function (s) {
        s.classList.remove('cq-screen--active');
      });
      root.querySelector('[data-screen="' + name + '"]').classList.add('cq-screen--active');
    }

    /* State */
    var scores = { single: 0, love: 0, married: 0, parent: 0, hustle: 0 };
    var cur = 0;
    var userName = '';
    var winnerKey = '';

    /* ── Intro logic ── */
    var nameInput = el('cq-uname');
    var startBtn = el('cq-start');

    nameInput.addEventListener('input', function () {
      var hasName = nameInput.value.trim().length > 0;
      startBtn.disabled = !hasName;
      startBtn.classList.toggle('cq-btn--disabled', !hasName);
    });

    startBtn.addEventListener('click', function () {
      userName = nameInput.value.trim() || 'You';
      scores = { single: 0, love: 0, married: 0, parent: 0, hustle: 0 };
      cur = 0;
      renderQuestion();
      showScreen('quiz');
    });

    /* ── Quiz logic ── */
    function renderQuestion() {
      var q = QUESTIONS[cur];
      el('cq-qnum').textContent = 'Question ' + (cur + 1) + ' of ' + QUESTIONS.length;
      el('cq-qtext').textContent = q.q;
      var prog = el('cq-prog');
      prog.style.width = Math.round((cur / QUESTIONS.length) * 100) + '%';
      prog.parentElement.setAttribute('aria-valuenow', cur);

      var optsEl = el('cq-opts');
      optsEl.innerHTML = '';
      var shuffled = q.opts.slice().sort(function () { return Math.random() - 0.5; });
      shuffled.forEach(function (o) {
        var btn = document.createElement('button');
        btn.className = 'cq-opt';
        btn.textContent = o.t;
        btn.setAttribute('role', 'listitem');
        btn.addEventListener('click', function () { pickAnswer(o.c, btn); });
        optsEl.appendChild(btn);
      });
    }

    function pickAnswer(chpterKey, btn) {
      root.querySelectorAll('.cq-opt').forEach(function (b) {
        b.classList.remove('cq-opt--selected');
      });
      btn.classList.add('cq-opt--selected');
      scores[chpterKey]++;
      setTimeout(function () {
        cur++;
        if (cur < QUESTIONS.length) {
          renderQuestion();
        } else {
          showResult();
        }
      }, 400);
    }

    /* ── Result logic ── */
    function showResult() {
      el('cq-prog').style.width = '100%';
      winnerKey = Object.entries(scores).sort(function (a, b) { return b[1] - a[1]; })[0][0];
      var ch = CHAPTERS[winnerKey];

      /* Canvas card */
      drawCard(el('cq-canvas'), ch, userName);

      /* Traits */
      var traitsEl = el('cq-traits');
      traitsEl.innerHTML = '<p class="cq-traits-label">Your chpter traits</p>' +
        ch.traits.map(function (t) {
          return '<div class="cq-trait-row">' +
            '<div class="cq-trait-dot" style="background:' + ch.accent + '"></div>' +
            '<p class="cq-trait-text">' + t + '</p>' +
            '</div>';
        }).join('');

      /* Email block colours */
      var emailBlock = el('cq-email-block');
      emailBlock.style.background = ch.captureBg;
      emailBlock.style.borderColor = ch.captureBorder;
      var claimBtn = el('cq-claim-btn');
      claimBtn.style.background = ch.btnBg;

      /* Chpter name in email sub */
      var nameSpan = el('cq-chpter-name');
      if (nameSpan) nameSpan.textContent = ' ' + ch.name + '.';

      /* Reset email form */
      el('cq-email-form').style.display = 'block';
      el('cq-discount').style.display = 'none';
      var emailInput = el('cq-email');
      emailInput.value = '';
      emailInput.classList.remove('cq-input--error');
      var errEl = el('cq-email-err');
      errEl.classList.remove('cq-email-err--visible');

      /* Shop button */
      var shopBtn = el('cq-shop-btn');
      shopBtn.style.background = ch.accent;
      var collectionUrl = root.dataset['collection' + ch.collectionKey.replace('url_', '').charAt(0).toUpperCase() + ch.collectionKey.replace('url_', '').slice(1)];
      if (!collectionUrl) {
        /* Try to read from section data attributes set via Liquid */
        var attrName = 'data-' + ch.collectionKey.replace('_', '-');
        collectionUrl = root.getAttribute(attrName);
      }
      if (collectionUrl) shopBtn.href = collectionUrl;

      /* Share buttons */
      el('cq-dl').onclick = function () { downloadCard(ch); };
      el('cq-copy-tag').onclick = function () { copyTag(ch); };
      el('cq-retry').onclick = function () { restart(); };

      showScreen('result');
    }

    /* ── Email submit ── */
    el('cq-claim-btn').addEventListener('click', function () {
      console.log('[ChptersQuiz] Claim button clicked');
      var emailVal = el('cq-email').value.trim();
      var errEl = el('cq-email-err');
      if (!isValidEmail(emailVal)) {
        el('cq-email').classList.add('cq-input--error');
        errEl.classList.add('cq-email-err--visible');
        return;
      }
      el('cq-email').classList.remove('cq-input--error');
      errEl.classList.remove('cq-email-err--visible');

      var ch = CHAPTERS[winnerKey];
      var claimBtn = el('cq-claim-btn');
      claimBtn.textContent = 'Saving...';
      claimBtn.disabled = true;

      console.log('[ChptersQuiz] Calling Klaviyo — listId:', settings.klaviyoListId, 'pubKey:', settings.klaviyoPublicKey, 'email:', emailVal);
      klaviyoSubscribe(emailVal, userName, ch.name, settings.klaviyoListId, settings.klaviyoPublicKey, function () {
        el('cq-email-form').style.display = 'none';
        var discountEl = el('cq-discount');
        discountEl.style.display = 'block';

        el('cq-confirmed-name').textContent = userName;

        var codeBox = el('cq-code-box');
        codeBox.style.background = ch.codeBg;
        codeBox.style.border = '1px solid ' + ch.codeBorder;

        var codeLabel = el('cq-code-label');
        codeLabel.style.color = ch.dark;

        var discountCode = settings.codes[winnerKey];
        var codeVal = el('cq-code-val');
        codeVal.textContent = discountCode;
        codeVal.style.color = ch.codeText;

        var copyBtn = el('cq-copy-btn');
        copyBtn.style.color = ch.codeBtnColor;
        copyBtn.style.borderColor = ch.codeBtnColor;
        copyBtn.onclick = function () { copyCode(discountCode, ch); };
      });
    });

    /* ── Share helpers ── */
    function downloadCard(ch) {
      var canvas = el('cq-canvas');
      var link = document.createElement('a');
      link.download = 'my-' + winnerKey + '-chpter.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
      flash(el('cq-share-confirm'), 'Image saved! Share it on Instagram or TikTok Stories ✓', '#0F6E56');
    }

    function copyTag(ch) {
      var text = ch.tag + ' — chpters.com | ' + ch.sub;
      copyToClipboard(text, function () {
        flash(el('cq-share-confirm'), 'Copied! Paste it in your bio or caption ✓', '#0F6E56');
      });
    }

    function copyCode(code, ch) {
      copyToClipboard(code, function () {
        flash(el('cq-copy-confirm'), 'Copied! Use it at checkout on chpters.com ✓', ch.mid);
      });
    }

    function restart() {
      nameInput.value = '';
      startBtn.disabled = true;
      startBtn.classList.add('cq-btn--disabled');
      showScreen('intro');
    }
  }

  /* ── Utilities ── */
  function isValidEmail(e) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }

  function copyToClipboard(text, cb) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(cb).catch(function () { fallbackCopy(text, cb); });
    } else {
      fallbackCopy(text, cb);
    }
  }

  function fallbackCopy(text, cb) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
    if (cb) cb();
  }

  function flash(el, msg, color) {
    if (!el) return;
    el.textContent = msg;
    el.style.color = color || '#0F6E56';
    setTimeout(function () { el.textContent = ''; }, 4500);
  }

  /* ── Export ── */
  window.ChptersQuiz = { init: init };

})();